import { cleanString, isMobile } from '@/helpers';
import _ from '@/lodash';
import ReactSelect from 'react-select';
import Modal from '../Modal/Modal';
import styles from '@/components/Modal/Modal.module.scss';
import { IoIosArrowDown } from 'react-icons/io';
import { useState } from 'react';

export interface SelectOptions {
  maxItems?: number;
  maxMenuHeight?: number;
  inputRef?: any;

  isDisabled?: boolean;
  isMulti?: boolean;
  isSearchable?: boolean;
  isOutline?: boolean;
  isClearable?: boolean;
  isRounded?: boolean;
  showMobileModal?: boolean;
}

type SelectProps = {
  options: any;
  onChange: Function;
  value?: any;
  placeholder?: string;
  className?: string;
} & SelectOptions;

function Select(props: SelectProps) {
  const {
    maxItems,
    onChange,
    maxMenuHeight = 315,
    isOutline = false,
    isClearable = false,
    isRounded = false,
    isSearchable = _.size(props.options) > 10 && !isMobile(),
    showMobileModal,
    ...passedProps
  } = props;

  const dark1 = 'oklch(99% 1% 45)';
  const dark3 = 'oklch(94% 1% 45)';
  const dark6 = 'oklch(60% 1% 45)';
  const dark8 = 'oklch(40% 1% 45)';
  const green500 = 'oklch(50% 17% 165)';
  const greenMuted800 = 'oklch(80% 8% 165)';
  const white = '#ffffff';

  const [mobileSearch, setMobileSearch] = useState('');

  if (isMobile() && showMobileModal) {
    const filteredOptions = _.filter(props.options, (option) =>
      _.includes(cleanString(option.label), cleanString(mobileSearch)),
    );

    return (
      <Modal
        isFullscreen
        buttonChildren={
          <div
            className={
              isOutline ? styles.mobileSelectOutline : styles.mobileSelect
            }
          >
            <span>{props.placeholder}</span>
            <IoIosArrowDown />
          </div>
        }
        render={({ closeButton, closeModal }) => (
          <div className={styles.modalForm}>
            {closeButton}

            <div className={styles.modalSubheader}>{props.placeholder}</div>

            <input
              className="form-input"
              placeholder="Search..."
              value={mobileSearch}
              onChange={(event) => setMobileSearch(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  props.onChange(_.first(filteredOptions));
                }
              }}
              autoFocus
            />

            {_.map(filteredOptions, (option) => (
              <div
                className={styles.mobileLink}
                onClick={() => {
                  props.onChange(option);
                  closeModal();
                }}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      />
    );
  }

  return (
    <ReactSelect
      isClearable={isClearable}
      maxMenuHeight={maxMenuHeight}
      menuPortalTarget={document.body}
      isSearchable={isSearchable}
      styles={{
        control: (provided: any, state: any) => ({
          ...provided,
          borderRadius: isRounded ? '36px' : '4px',
          border: isOutline
            ? `2px solid ${greenMuted800}`
            : `2px solid ${dark3}`,
          ':hover': isOutline ? { borderColor: white } : { borderColor: dark6 },
          backgroundColor: isOutline ? green500 : white,
          boxShadow: state.isFocused
            ? isOutline
              ? `0 0 0 1px ${greenMuted800}`
              : `0 0 0 1px ${dark6}`
            : 'none',
          cursor: 'pointer',
          padding: isOutline ? '0.4em 0.8em' : provided.padding,
        }),
        clearIndicator: (provided: any) =>
          isOutline
            ? {
                ...provided,
                color: greenMuted800,
                padding: '4px',
                ':focused': { color: white },
                ':hover': { color: white },
              }
            : provided,
        dropdownIndicator: (provided: any) =>
          isOutline
            ? {
                ...provided,
                color: greenMuted800,
                padding: '4px',
                ':focused': { color: white },
                ':hover': { color: white },
              }
            : provided,
        option: (provided: any, state: any) => {
          const isMaxItems =
            maxItems && _.size(state.selectProps.value) >= maxItems;

          return {
            ...provided,
            opacity: isMaxItems ? 0.3 : 1,
            backgroundColor: state.isFocused && !isMaxItems ? dark1 : white,
            ':active': isMaxItems ? {} : { backgroundColor: dark3 },
            color: dark8,
            cursor: isMaxItems ? 'not-allowed' : 'pointer',
          };
        },
        menu: (provided: any) => ({
          ...provided,
          borderRadius: '6px',
          backgroundColor: white,
          border: 'none',
          boxShadow: '0 3px 1.71429rem oklch(40% 1% 165 / 0.2)',
          minWidth: 150,
          overflow: 'hidden',
        }),
        multiValue: (provided: any) => ({
          ...provided,
          backgroundColor: dark3,
          borderRadius: isRounded ? '8px' : '4px',
          fontSize: '16px',
        }),
        placeholder: (provided: any) => ({
          ...provided,
          color: isOutline ? dark1 : dark6,
          fontWeight: isOutline ? 600 : 400,
          top: '48%',
        }),
        valueContainer: (provided: any) => ({
          ...provided,
          padding: '0 0.5em',
        }),
        indicatorSeparator: (provided: any) =>
          isClearable
            ? isOutline
              ? { ...provided, width: '2px', backgroundColor: greenMuted800 }
              : provided
            : { display: 'none' },
        input: (provided: any) =>
          isOutline ? { ...provided, color: white } : provided,
        singleValue: (provided: any) =>
          isOutline ? { ...provided, color: white } : provided,
      }}
      onChange={(selection: any) => {
        if (!maxItems || _.size(selection) <= maxItems) {
          onChange(selection);
        }
      }}
      aria-label={props.placeholder}
      {...passedProps}
    />
  );
}

export default Select;
